import { FontPreloadLinks, type FontPreloadLinksProps } from './FontPreloadLinks';
import {
    createCSSPreloadLinks,
    getRemotePagesCSSAssets,
    type RemotePageCSSAssetsResult,
} from './getRemotePageCSSAssets';

export type DocumentHeadAssetsProps = FontPreloadLinksProps & {
    remotePageCSSAssets: RemotePageCSSAssetsResult | null;
};

/**
 * Emits font preload hints before any stylesheet links.
 * Must render from `_document` (not `next/head`) so Next.js does not reorder CSS
 * `rel="preload" as="style"` tags ahead of font preloads.
 */
export function DocumentHeadAssets({ lob, remotePageCSSAssets }: DocumentHeadAssetsProps) {
    return (
        <>
            <FontPreloadLinks lob={lob} />
            {createCSSPreloadLinks(remotePageCSSAssets)}
        </>
    );
}

export async function fetchRemotePageCSSAssetsForPathname(
    pathname: string,
    moduleMap: Readonly<Record<string, string | readonly string[]>>,
): Promise<RemotePageCSSAssetsResult | null> {
    const remoteModules = moduleMap[pathname];
    if (!remoteModules?.length) {
        return null;
    }

    return getRemotePagesCSSAssets(remoteModules);
}

/**
 * Fetches remote CSS for `_app` client-side navigations only.
 * SSR initial load is handled by `_document` to preserve font/CSS ordering.
 */
export async function fetchRemotePageCSSAssetsForRemoteModule(
    remoteModules?: string | readonly string[],
): Promise<RemotePageCSSAssetsResult | null> {
    if (globalThis.window === undefined || !remoteModules?.length) {
        return null;
    }

    return getRemotePagesCSSAssets(remoteModules);
}
